/**
 * Auth module — init, identify, session state.
 *
 * Defers all token management to Supabase (persistSession + autoRefresh
 * in the client config). The SDK adds a thin layer on top to:
 *   - Auto sign-in anonymously on first init with no session.
 *   - Load + cache the current player's profile row.
 *   - Keep `isAuthenticated()` synchronous for render-path use.
 *   - Expose `identify()` for the claim-account flow.
 */
import type { Player, IdentifyOptions } from './types';
import { VibecadeError, VibecadeErrorCode } from './types';
import type { SdkContext } from './internal/context';
import { wrapError } from './internal/errors';
import { withReadTimeout } from './internal/readTimeout';
import { isInIframe } from './internal/supabaseClient';

/**
 * Non-functional refresh-token sentinel used ONLY inside an embedded
 * game iframe.
 *
 * Supabase's `setSession()` requires a non-empty `refresh_token`, but the
 * iframe must NEVER hold the launcher's real refresh token: game code is
 * untrusted (third-party origins), and a refresh token is a renewable
 * credential — leaking it to the game enables persistent account takeover
 * (mint fresh access tokens indefinitely against the public anon key).
 *
 * Instead the iframe adopts only a short-lived ACCESS token, runs with
 * `autoRefreshToken: false` (see internal/supabaseClient.ts), and the
 * launcher — the sole holder of the real refresh token, in its own
 * origin — pushes a freshly-rotated access token over the
 * `vibecade:session` channel whenever its session refreshes. This
 * sentinel only satisfies the `setSession` signature; it is never used to
 * refresh (auto-refresh is off and we never call refreshSession), so it
 * never leaves the iframe and reveals nothing if it did.
 */
export const IFRAME_PLACEHOLDER_REFRESH_TOKEN = 'vibecade-iframe-no-refresh-token';

/**
 * How much life an embedded game's access token must have left before
 * `getAccessToken` will hand it out.
 *
 * Matches auth-js's own EXPIRY_MARGIN_MS (AUTO_REFRESH_TICK_THRESHOLD *
 * AUTO_REFRESH_TICK_DURATION_MS = 90s at the time of writing), because that is
 * the threshold at which auth-js stops treating a session as usable and
 * reaches for a refresh. Inside an iframe we are not allowed to take that
 * branch, so we decline at the same point instead of handing back a token that
 * is about to be refused by the server anyway.
 */
const IFRAME_TOKEN_MIN_LIFETIME_MS = 90_000;

/**
 * How long an SDK request holding an EXPIRED embedded token waits for the
 * launcher to push a fresh one before going out with what it has.
 *
 * The launcher pushes on its own refresh, normally a minute or more before
 * expiry. A token that has actually run out means the launcher missed that,
 * almost always because the tab slept, and it refreshes the moment the tab
 * wakes: the same moment the game resumes and submits. A few seconds covers
 * that race. Waited once per token, so a launcher that has gone away costs
 * one delay, not one per request.
 */
const ROTATION_WAIT_MS = 5_000;

/** Treated as expired this close to expiry: it would run out in flight. */
const EXPIRY_SKEW_MS = 5_000;

/**
 * The `exp` of a JWT in milliseconds, or null when it cannot be read. The
 * signature is not checked: this decides only whether a token is worth
 * handing to setSession, and the server checks everything else.
 */
function jwtExpiresAtMs(token: string): number | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Anonymous sign-ins in flight, keyed by auth scope.
 *
 * Module scope on purpose: the clients that need to coordinate are separate
 * `Vibecade` instances with separate contexts, and the whole reason they must
 * coordinate is that they share one identity slot. Per-instance state could not
 * see the other instance. See the use site in `doInit` for what goes wrong
 * without it — briefly, two clients on one scope each finding no session and
 * each creating a player.
 */
const signIns = new Map<string, Promise<unknown>>();

/**
 * Register a write to a scope's identity slot, so anything entering `init()`
 * waits for it instead of reading the slot mid-change.
 *
 * The entry covers every registration still in flight, not only the newest.
 * That distinction is the whole point of this helper. Holding just the newest
 * promise looks right until two pre-init clients on one scope adopt at once
 * and the SECOND settles first: its own cleanup then empties the map while the
 * FIRST write is still pending, an `init()` entering that window waits for
 * nothing, caches the second session, and the first write lands on the shared
 * slot afterwards. Cached one person, signed in as another.
 *
 * So each registration chains onto whatever was already there, and the entry
 * is removed only once everything it covers has settled, and only if a later
 * registration has not already replaced it (that one owns the cleanup). Never
 * rejects: a failed write must still release whoever is waiting, because they
 * are waiting to find out what is in the slot, not to be told it went well.
 *
 * Read and written with no await between, which is what makes it a rendezvous
 * rather than a race.
 */
function holdScope(scope: string, work: Promise<unknown>): void {
  const prior = signIns.get(scope);
  const settled = work.then(
    () => {},
    () => {},
  );
  const entry: Promise<void> = prior
    ? Promise.all([prior.then(() => {}, () => {}), settled]).then(() => {})
    : settled;
  signIns.set(scope, entry);
  void entry.then(() => {
    if (signIns.get(scope) === entry) signIns.delete(scope);
  });
}

/**
 * Auth scopes on which some client has entered `init()`.
 *
 * Module scope for the same reason as `signIns`, and read by `adoptSession` to
 * refuse a late adoption. An instance-local flag is not enough: a game that
 * owns several game ids builds a client per board sharing one `authScope`, so
 * the client adopting a session and the client that already signed in
 * anonymously are different objects looking at one identity slot. Checked
 * per-scope, the sibling's `init()` is visible; checked per-instance, it is not,
 * and the two clients end up as two people.
 *
 * Never cleared, including when an init fails, because a failed init may still
 * have created the player this guard exists to protect.
 */
const initStartedScopes = new Set<string>();

/**
 * Compute the embedding launcher's origin so we can validate inbound
 * `vibecade:session` messages by `event.origin`. Two sources, in
 * preference order:
 *
 *   1. `window.location.ancestorOrigins[0]` — non-standard but
 *      supported by Safari + Chromium. Returns the immediate parent's
 *      origin even when document.referrer is empty (e.g. when the
 *      launcher's Referrer-Policy is `no-referrer`).
 *   2. `document.referrer` — every browser exposes this. The
 *      launcher's Referrer-Policy `strict-origin-when-cross-origin`
 *      sends the bare origin (no path) for cross-origin iframe loads,
 *      which is exactly what we need.
 *
 * Returns null when neither is available (privacy-hardened browsers,
 * unusual embedding contexts, or running outside a window). Callers
 * fail closed on null — refusing an unvalidated session is preferable
 * to accepting tokens from an unknown sender.
 */
function expectedLauncherOrigin(): string | null {
  if (typeof window === 'undefined') return null;

  // ancestorOrigins isn't in every TS lib.dom set; cast through a
  // narrow shape rather than escalating to `any`. The runtime check
  // (`length > 0`) guards against the empty list a top-level page
  // would expose.
  const ancestors = (window.location as Location & { ancestorOrigins?: DOMStringList })
    .ancestorOrigins;
  if (ancestors && ancestors.length > 0) {
    const a = ancestors[0];
    if (typeof a === 'string' && a.length > 0) return a;
  }

  if (typeof document !== 'undefined' && document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      /* malformed referrer — fall through to null */
    }
  }
  return null;
}

/**
 * Origins trusted as a launcher without asking the platform: the iOS app's
 * webview (Capacitor's default scheme), and loopback for local development.
 * A page on the player's own loopback is not somewhere an attacker can put
 * one, which is what makes these safe to trust unconditionally.
 */
const CAPACITOR_ORIGIN = 'capacitor://localhost';

/**
 * How long a launcher's answer waits on the trust lookup before the game
 * gives up and signs in anonymously. Separate from the 2s handshake window,
 * which only bounds whether anyone answers. Shorter than the lookup's own 10s
 * read timeout, because this delays the game's start.
 */
const LAUNCHER_CHECK_WAIT_MS = 5_000;
const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

function isBuiltInLauncherOrigin(origin: string): boolean {
  if (origin === CAPACITOR_ORIGIN) return true;
  try {
    const url = new URL(origin);
    return (url.protocol === 'http:' || url.protocol === 'https:') &&
      LOOPBACK_HOSTNAMES.has(url.hostname);
  } catch {
    return false;
  }
}

export class AuthModule {
  private authenticated = false;
  private initPromise: Promise<void> | null = null;
  private authSub: { unsubscribe: () => void } | null = null;
  // Detaches the persistent listener that adopts launcher-pushed access
  // token rotations while embedded. Null when standalone or disposed.
  private parentTokenUnsub: (() => void) | null = null;
  // Who this module believes is in the shared identity slot, and a counter
  // that retires a reload the session has since moved past. Both exist for
  // the listener below.
  private sessionUserId: string | null = null;
  private playerLoadGen = 0;
  // The live session's tokens and expiry, mirrored from onAuthStateChange.
  // Read by the embedded paths of getAccessToken and requestToken, which
  // cannot call getSession() — see getAccessToken for why. The refresh token
  // is mirrored only to tell whether the session is the embedded one.
  private sessionAccessToken: string | null = null;
  private sessionRefreshToken: string | null = null;
  private sessionExpiresAtMs: number | null = null;
  // Whether the live session's user is anonymous, from the same events. Read
  // by identify(), which claims an anonymous player rather than signing in,
  // and by the listener, to notice a claim finishing. Null with no session.
  private sessionIsAnonymous: boolean | null = null;
  // A claim finished but the player row has not been reloaded since. Kept
  // apart from sessionIsAnonymous so a failed reload can be retried on a later
  // event without pretending the session is still anonymous to identify().
  private claimReloadOwed = false;
  // Requests waiting for the launcher to push a token, and the one wait in
  // progress (or finished) for the current expired token. See requestToken.
  private rotationWaiters: Array<() => void> = [];
  private expiredWait: { token: string; done: Promise<void> } | null = null;
  // Whether this module has ever established an identity. Set once and never
  // cleared, INCLUDING on sign-out: after a sign-out the module still knows
  // that init has been and gone, so the next session is somebody arriving
  // rather than init's own sign-in, and its player has to be loaded.
  private everEstablished = false;

  constructor(private readonly ctx: SdkContext) {
    // Track auth state changes so isAuthenticated stays in sync with
    // whatever Supabase does (token refresh, sign-out, tab sync, etc.).
    // Holding the subscription handle lets dispose() detach it; without
    // this, repeatedly constructing + discarding Vibecade instances on
    // one page leaks listeners.
    const { data } = this.ctx.sb.auth.onAuthStateChange((_event, session) => {
      this.authenticated = !!session?.user;
      // Mirrored before every early return below, so a plain token refresh —
      // which changes the token and nothing else, and is the common event —
      // is still reflected for getAccessToken's embedded path.
      this.sessionAccessToken = session?.access_token ?? null;
      this.sessionRefreshToken = session?.refresh_token ?? null;
      this.sessionExpiresAtMs = session?.expires_at ? session.expires_at * 1000 : null;
      const wasAnonymous = this.sessionIsAnonymous;
      this.sessionIsAnonymous = session?.user ? session.user.is_anonymous === true : null;
      // Anything waiting on a rotation re-reads the mirror. Woken on every
      // event, not only a new token: a sign-out is an answer too.
      const waiters = this.rotationWaiters;
      this.rotationWaiters = [];
      for (const wake of waiters) wake();
      if (!session) {
        this.sessionUserId = null;
        // Bumped so a reload still in flight cannot land after a sign-out.
        this.playerLoadGen++;
        this.ctx.player.current = null;
        return;
      }
      // A session for a DIFFERENT user replaced ours, so the cached player is
      // now somebody else. This is what happens when another tab on the same
      // authScope adopts a patron session: storage is shared, auth-js syncs
      // the new session into this tab, and until this line the cached player
      // stayed anonymous while every request went out with the adopted JWT.
      // The damage is quiet and downstream - a cloud save keyed on the cached
      // id fails RLS, an around-me leaderboard read centres on the wrong
      // person - so it is worth a round trip whenever the id actually moves.
      //
      // Only on a CHANGE of user: a token refresh fires this listener
      // constantly and re-reading the same player row each time would be a
      // request per refresh for no new information.
      const userId = session.user?.id;
      if (!userId) return;
      if (userId === this.sessionUserId) {
        // The same player stopped being anonymous: identify()'s claim link was
        // clicked, perhaps on another device, and this session has refreshed
        // since. Reloaded so getPlayer().isAnonymous says so. The cache is NOT
        // cleared first, unlike a change of user below: it is still this
        // player, and only one flag on it is out of date.
        if (wasAnonymous === true && this.sessionIsAnonymous === false) {
          this.claimReloadOwed = true;
        }
        if (this.claimReloadOwed && this.sessionIsAnonymous === false) {
          // Owed until one succeeds: a failed reload leaves the cached row
          // (stale in one flag only) and the flag set, so the next event for
          // this user, an ordinary token refresh will do, tries again.
          const gen = ++this.playerLoadGen;
          this.loadPlayer(userId, gen).then(
            () => { if (gen === this.playerLoadGen) this.claimReloadOwed = false; },
            () => {},
          );
        }
        return;
      }

      // Tracked against the SESSION's user and not the cached player, because
      // the cache is empty for the whole window between one change and its
      // reload landing. Keyed on the cache, a B -> C change during that window
      // saw no cached player to disagree with and started no reload at all,
      // leaving C signed in with nobody loaded.
      const known = this.everEstablished;
      this.sessionUserId = userId;
      // Seeing a session at all counts as established, so the very next change
      // reloads rather than being mistaken for init's sign-in a second time.
      this.everEstablished = true;
      // Nobody established yet: this is init's own sign-in, and init loads the
      // player itself. Reloading here would only duplicate that read.
      //
      // Keyed on everEstablished rather than on the current session user,
      // because sign-out clears the latter. Keyed that way, a client that
      // signed out and then received a session again - another tab signing in,
      // or adopting - took this early return, and was left reporting
      // authenticated with getPlayer() null for the rest of the page's life.
      if (!known) return;

      const gen = ++this.playerLoadGen;
      this.ctx.player.current = null;
      // Fire and forget: this is a listener, not a caller, and a failed reload
      // must leave the cache empty rather than stale. getPlayer() answering
      // null is honest; answering the previous person is not. The generation
      // is what stops a slow reload from writing its answer after the session
      // moved on again.
      this.loadPlayer(userId, gen).catch(() => {
        // A failed read must not be REMEMBERED as "this user is loaded". The
        // early return above keys on sessionUserId, so leaving it set turns
        // every later event for this user into a no-op: one transient timeout
        // and the cache stays empty for the rest of the page's life, with
        // cloud saves and around-me reads failing and no public way to retry.
        //
        // Un-recording it makes the next event for the same user, an ordinary
        // token refresh will do, take the reload branch again. Guarded by the
        // generation so a stale failure cannot clear the tracking of a user
        // who arrived after it.
        if (gen === this.playerLoadGen) this.sessionUserId = null;
      });
    });
    this.authSub = data.subscription;
  }

  /** Stop tracking Supabase auth state. Called by Vibecade.dispose(). */
  dispose(): void {
    this.authSub?.unsubscribe();
    this.authSub = null;
    this.parentTokenUnsub?.();
    this.parentTokenUnsub = null;
    // No push can reach a disposed module, so a request waiting for one is
    // released now rather than at the end of ROTATION_WAIT_MS.
    const waiters = this.rotationWaiters;
    this.rotationWaiters = [];
    for (const wake of waiters) wake();
  }

  /**
   * Idempotent. Concurrent calls share one in-flight promise; failures
   * clear it so callers can retry.
   */
  init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    // Recorded before the first await so a scope-mate's adoptSession() sees it
    // even if that call arrives in the same tick.
    initStartedScopes.add(this.ctx.config.authScope);
    this.initPromise = this.doInit().catch((e) => {
      this.initPromise = null;
      throw e;
    });
    return this.initPromise;
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  getPlayer(): Player | null {
    return this.ctx.player.current;
  }

  /**
   * Claim the account: attach an email or OAuth identity to the player.
   *
   * For an ANONYMOUS player this must attach the identity to the user that
   * already exists, which is what keeps their id, scores, achievements and
   * saves. That is `updateUser({ email })` and `linkIdentity({ provider })`.
   * The sign-in calls this used before (`signInWithOtp`, `signInWithOAuth`)
   * do not look at the current session at all: they create or sign in to a
   * SEPARATE user, and the anonymous one is left behind with everything it
   * owned. Verified against GoTrue v2.177.0: an anonymous user A calling
   * signInWithOtp and clicking the link ends up signed in as a new user B,
   * with A still in auth.users, still anonymous.
   *
   * Two cases still sign in rather than claim, as before:
   *   - The email already has an account (`email_exists`). This is a
   *     returning player on a new device. Their anonymous progress here is
   *     not merged into it; nothing server-side could do that.
   *   - OAuth when the project has manual linking disabled
   *     (`manual_linking_disabled`), which linkIdentity requires. Logged,
   *     because the anonymous player is left behind exactly as before.
   *
   * A player who is not anonymous, or who has no session yet, signs in.
   */
  async identify(opts: IdentifyOptions): Promise<void> {
    if (!opts || typeof opts !== 'object') {
      throw new VibecadeError(
        VibecadeErrorCode.InvalidConfig,
        'identify() requires { email } or { provider }',
      );
    }
    try {
      if ('email' in opts) {
        if (typeof opts.email !== 'string' || !opts.email) {
          throw new VibecadeError(VibecadeErrorCode.InvalidConfig, 'identify: email is required');
        }
        if (await this.isAnonymousSession()) {
          // Sends a confirmation link to the address. Clicking it, on any
          // device, verifies the email on THIS user, and the platform's
          // on_auth_user_updated trigger flips players.is_anonymous.
          const { error } = await this.ctx.sb.auth.updateUser({ email: opts.email });
          if (!error) {
            this.ctx.logger.debug('identify: claim link sent', opts.email);
            return;
          }
          if (error.code !== 'email_exists') {
            throw new VibecadeError(VibecadeErrorCode.AuthFailed, error.message);
          }
          this.ctx.logger.debug(
            'identify: email already has an account; signing in to it instead of claiming',
          );
        }
        const { error } = await this.ctx.sb.auth.signInWithOtp({
          email: opts.email,
          options: { shouldCreateUser: true },
        });
        if (error) {
          throw new VibecadeError(VibecadeErrorCode.AuthFailed, error.message);
        }
        this.ctx.logger.debug('identify: magic link sent', opts.email);
        return;
      }
      if ('provider' in opts) {
        if (await this.isAnonymousSession()) {
          // Redirects to the provider; the callback attaches the identity to
          // THIS user.
          const { error } = await this.ctx.sb.auth.linkIdentity({ provider: opts.provider });
          if (!error) {
            this.ctx.logger.debug('identify: OAuth link redirect initiated', opts.provider);
            return;
          }
          if (error.code !== 'manual_linking_disabled') {
            throw new VibecadeError(VibecadeErrorCode.AuthFailed, error.message);
          }
          this.ctx.logger.debug(
            'identify: manual linking is disabled on this project, so OAuth signs in as a ' +
              'new user and this anonymous player is not kept',
          );
        }
        const { error } = await this.ctx.sb.auth.signInWithOAuth({ provider: opts.provider });
        if (error) {
          throw new VibecadeError(VibecadeErrorCode.AuthFailed, error.message);
        }
        this.ctx.logger.debug('identify: OAuth redirect initiated', opts.provider);
        return;
      }
      throw new VibecadeError(
        VibecadeErrorCode.InvalidConfig,
        'identify: supply either { email } or { provider }',
      );
    } catch (e) {
      throw wrapError(e, VibecadeErrorCode.AuthFailed, 'identify() failed');
    }
  }

  /**
   * Whether the live session is anonymous, for identify(). The mirror answers
   * once any auth event has arrived. Before that (a session restored from
   * storage whose INITIAL_SESSION has not been delivered yet) it is null, and
   * reading null as "not anonymous" would send the claim down the sign-in
   * path this exists to avoid, so the session is read directly. Standalone
   * only: in an iframe getSession() can spend the placeholder refresh token,
   * and an iframe has no session to restore before its first event anyway.
   */
  private async isAnonymousSession(): Promise<boolean> {
    if (this.sessionIsAnonymous !== null) return this.sessionIsAnonymous;
    if (isInIframe()) return false;
    const { data } = await this.ctx.sb.auth.getSession();
    return data.session?.user?.is_anonymous === true;
  }

  /**
   * Adopt a session a platform endpoint minted for this player.
   *
   * The case this exists for: a game sends the player through a third-party
   * identity flow the SDK knows nothing about (Bloodline's Patreon link), a
   * platform Edge Function authenticates them and mints a Supabase session for
   * the player it resolved, and the browser has to end up holding THAT session
   * so every existing path — score submission, RLS, cloud saves — keeps
   * working against the right identity rather than an anonymous one.
   *
   * Call this BEFORE `init()`. `init()` signs in anonymously when it finds no
   * session, so adopting afterwards creates an anonymous player and abandons
   * it a moment later, leaving an orphan row and a window in which the game is
   * signed in as the wrong person. That ordering is ENFORCED rather than only
   * documented: a late call throws instead of quietly leaving that wreckage
   * behind, because the damage is to data and would be found much later.
   *
   * REFUSED INSIDE AN IFRAME, which is the invariant at the top of this file
   * rather than a limitation: an embedded game must never hold a renewable
   * credential, and there the launcher is the sole refresh authority. A game
   * that needs this is standalone on its own origin, where it already owns its
   * session.
   *
   * These tokens are a real, renewable session. Only ever pass something a
   * platform endpoint just handed you over TLS — a token that arrived
   * somewhere copyable, like a URL, is not that.
   */
  async adoptSession(tokens: {
    accessToken: string;
    refreshToken: string;
  }): Promise<Player | null> {
    if (
      !tokens || typeof tokens.accessToken !== 'string' || !tokens.accessToken ||
      typeof tokens.refreshToken !== 'string' || !tokens.refreshToken
    ) {
      throw new VibecadeError(
        VibecadeErrorCode.InvalidConfig,
        'adoptSession() requires { accessToken, refreshToken }',
      );
    }
    if (isInIframe()) {
      throw new VibecadeError(
        VibecadeErrorCode.AuthFailed,
        'adoptSession() is not available to an embedded game: the launcher owns the session',
      );
    }
    // The ordering rule, enforced. Refusing is better than adopting late: by
    // then an anonymous player may exist and may already own scores, and
    // switching identity underneath that leaves an orphan row plus a split
    // score history nobody notices for weeks.
    //
    // Checked PER SCOPE rather than per instance, because a game that owns
    // several game ids builds a client per board sharing one `authScope`, and
    // then the client adopting and the client that signed in anonymously are
    // different objects sharing one identity slot: an instance-local check
    // reads false while a sibling has already made the player this guard is
    // about. A stored session from an earlier visit is NOT a reason to refuse,
    // so this reads init state and not `authenticated` — a returning patron
    // whose last visit left an anonymous session behind is exactly who this is
    // for.
    if (this.initPromise || initStartedScopes.has(this.ctx.config.authScope)) {
      throw new VibecadeError(
        VibecadeErrorCode.InvalidConfig,
        'adoptSession() must be called before init(), on every client sharing this authScope',
      );
    }
    // Published to scope-mates BEFORE the await, in the `signIns` rendezvous
    // and for its exact reason: this is a write to the shared identity slot,
    // and a client entering init() while it is in flight must wait for it
    // rather than read the slot as it was and sign in anonymously over the
    // top. Refusing a LATER adoption is only half the guard; the other half
    // is that an init racing THIS one has somewhere to wait.
    //
    // Registered with no await between reading the map and writing it, which
    // is the whole of the guarantee.
    const scope = this.ctx.config.authScope;
    const pending = this.ctx.sb.auth.setSession({
      access_token: tokens.accessToken,
      refresh_token: tokens.refreshToken,
    });
    holdScope(scope, pending);
    try {
      const { data, error } = await pending;
      if (error || !data.user) {
        throw new VibecadeError(
          VibecadeErrorCode.AuthFailed,
          error?.message ?? 'The session was rejected',
        );
      }
      this.authenticated = true;
      // Stamped, like the listener's reloads. Without a generation this read
      // could finish after a newer session had already been resolved by
      // another context and write the adopted-but-now-obsolete user over it -
      // the same wrong-player state the listener guards against, reached
      // through the door adoption itself opens. Taken here rather than before
      // setSession so it is newer than any bump that landing the session
      // caused.
      const gen = ++this.playerLoadGen;
      const player = await this.loadPlayer(data.user.id, gen);
      this.ctx.logger.debug('adopted a platform-minted session', data.user.id);
      return player;
    } catch (e) {
      throw wrapError(e, VibecadeErrorCode.AuthFailed, 'adoptSession() failed');
    }
    // No `finally` clearing the rendezvous. holdScope owns that, and it has to:
    // the entry covers every concurrent write to this scope, so it can only be
    // removed once they have ALL settled, which is not a fact this call knows.
    // Clearing it from here retired an entry that a scope-mate's still-pending
    // adoption was also relying on.
  }

  /**
   * The current session's access token, or null when there is no session.
   *
   * The companion to `adoptSession`, and it exists for the same shape: a game
   * that runs its own identity flow has to call a PLATFORM endpoint as this
   * player, and until now nothing let it. Bloodline's pack play is the first:
   * its START names which patron is spending a try, and the endpoint reads
   * that from the bearer token. Without this the game's only options were to
   * parse auth-js's private session JSON out of localStorage, or to hold the
   * token from its own linking exchange and lose it on the next page load.
   *
   * Read through `getSession()`, so a token close to expiry comes back
   * refreshed rather than about to be rejected. Never throws: a caller in a
   * render path or a fire-and-forget report should treat "no token" and "could
   * not get one" the same way, which is to send the request without it and let
   * the endpoint decide.
   *
   * SEND IT ONLY TO THE PLATFORM. It authenticates as this player for its
   * lifetime; handing it to a third-party host is handing over the account.
   * The refresh token is deliberately NOT exposed: a short-lived credential
   * that expires on its own is a different thing from a renewable one.
   */
  async getAccessToken(): Promise<string | null> {
    try {
      // EMBEDDED: answer from the mirrored session, never through
      // getSession(). auth-js refreshes any session within EXPIRY_MARGIN_MS of
      // expiry from inside getSession, and that is NOT gated on the
      // autoRefreshToken option — its own comment says the branch exists for
      // when auto-refresh is off. In an iframe the refresh token is
      // IFRAME_PLACEHOLDER_REFRESH_TOKEN, so that refresh fails with a
      // non-retryable auth error, and auth-js answers a non-retryable failure
      // by REMOVING the session. Calling getAccessToken near expiry would
      // therefore sign the player out — the exact failure autoRefreshToken:
      // false exists to prevent (see internal/supabaseClient.ts), reached
      // through a different door. Declining is the honest answer here: the
      // launcher is the refresh authority and pushes a fresh token on its own
      // rotation, so the caller retrying shortly gets a live one.
      if (this.answersFromMirror()) {
        if (!this.sessionAccessToken) return null;
        // A session with no expiry is not expiring: auth-js reads a missing
        // expires_at as "not expired" too, so mirror that rather than
        // inventing a refusal it would not make.
        if (
          this.sessionExpiresAtMs !== null &&
          this.sessionExpiresAtMs - Date.now() < IFRAME_TOKEN_MIN_LIFETIME_MS
        ) {
          this.ctx.logger.debug('getAccessToken: embedded token too close to expiry');
          return null;
        }
        return this.sessionAccessToken;
      }
      const { data, error } = await this.ctx.sb.auth.getSession();
      if (error) {
        this.ctx.logger.debug('getAccessToken: session read failed', error.message);
        return null;
      }
      return data.session?.access_token ?? null;
    } catch {
      return null;
    }
  }

  /**
   * The token the SDK's OWN requests carry: every Edge Function call, and
   * every read and write through `ctx.db`. Internal; the public accessor is
   * getAccessToken.
   *
   * Standalone this is getSession(), which refreshes a token close to expiry,
   * exactly as before. EMBEDDED it must not be, for the reason getAccessToken
   * gives: getSession() near expiry spends the placeholder refresh token, the
   * refresh is refused, and auth-js removes the session. Before this existed
   * every request took that path, so the care getAccessToken takes covered one
   * door out of three.
   *
   * Embedded, the answer is the pushed token, sent while it lasts. Unlike
   * getAccessToken this does not decline a token inside the 90s margin: it is
   * still valid, and a request is better sent with it than not sent. Only a
   * token that has actually run out waits, briefly, for the launcher's push
   * (see ROTATION_WAIT_MS); if none comes it goes anyway and the server's
   * refusal is the error the caller sees, with the session still in place for
   * the push that follows.
   */
  async requestToken(): Promise<string | null> {
    if (!this.answersFromMirror()) {
      const { data } = await this.ctx.sb.auth.getSession();
      return data.session?.access_token ?? null;
    }
    const token = this.sessionAccessToken;
    if (
      token &&
      this.sessionExpiresAtMs !== null &&
      this.sessionExpiresAtMs - Date.now() < EXPIRY_SKEW_MS
    ) {
      // Shared by every request that finds this same token expired, and kept
      // once finished, so later requests await an already-settled promise and
      // go straight out instead of each waiting again.
      if (this.expiredWait?.token !== token) {
        const done = new Promise<void>((resolve) => {
          const finish = () => {
            clearTimeout(timer);
            resolve();
          };
          const timer = setTimeout(finish, ROTATION_WAIT_MS);
          this.rotationWaiters.push(finish);
        });
        this.expiredWait = { token, done };
      }
      await this.expiredWait.done;
    }
    return this.sessionAccessToken;
  }

  /**
   * Whether the live session is the embedded one, which auth-js cannot
   * refresh: it carries IFRAME_PLACEHOLDER_REFRESH_TOKEN. Keyed on the token
   * rather than on isInIframe(), because an embedded game whose handshake
   * failed signs in anonymously with a real refresh token, and that session
   * should refresh normally.
   */
  private isEmbeddedSession(): boolean {
    return this.sessionRefreshToken === IFRAME_PLACEHOLDER_REFRESH_TOKEN;
  }

  /**
   * Whether a token question must be answered from the mirror, never through
   * getSession(): the embedded session, and ALSO an iframe with no session yet.
   *
   * The second case is a race, not a state. A getSession() started while the
   * slot is empty waits on auth-js's lock, and the handshake's setSession can
   * take the lock first. The queued call then reads the embedded session, and
   * if it is near expiry, spends the placeholder exactly as above. Reads made
   * before init() has finished are enough to start one, and supabase-js makes
   * one itself while the data client is built. With nothing mirrored there is
   * no token to send anyway, so answering null from here loses nothing.
   */
  private answersFromMirror(): boolean {
    return this.isEmbeddedSession() || (isInIframe() && !this.sessionAccessToken);
  }

  // -------------------------------------------------------------------------
  // private
  // -------------------------------------------------------------------------

  private async doInit(): Promise<void> {
    try {
      // 1) Try to adopt the embedding launcher's session via postMessage.
      //    This is what unifies launcher + game identity: without it each
      //    SDK instance signs in anonymously and ends up as a different
      //    auth.users row. With it, the game iframe shares the launcher's
      //    identity, so display-name edits, achievement progress, cloud
      //    saves etc. are all attributed to the same person.
      //
      //    No-op when the game isn't embedded (window.parent === window)
      //    or when the launcher doesn't respond within the handshake
      //    window; falls through to the existing flow below.
      const parent = await this.tryReceiveParentSession();
      if (parent) {
        // Adopt ONLY the access token. The placeholder refresh token
        // keeps `setSession` happy without ever giving game code a
        // renewable credential; the launcher pushes rotated access
        // tokens via subscribeToParentTokenUpdates() below.
        const { data, error } = await this.ctx.sb.auth.setSession({
          access_token: parent.access_token,
          refresh_token: IFRAME_PLACEHOLDER_REFRESH_TOKEN,
        });
        if (!error && data.user) {
          this.authenticated = true;
          await this.loadPlayer(data.user.id, ++this.playerLoadGen);
          // Keep listening for the launcher to push freshly-rotated
          // access tokens (it is the sole refresh authority). Pinned to
          // the same launcher origin we validated during the handshake.
          this.subscribeToParentTokenUpdates(parent.origin);
          this.ctx.logger.debug('adopted parent session', data.user.id);
          return;
        }
        this.ctx.logger.debug(
          'parent session rejected, falling back to local flow',
          error?.message,
        );
      }

      // 2) Existing flow: reuse cached session, or sign in anonymously.
      //
      // Wait for any identity write already in flight on this scope BEFORE
      // reading the slot, and note that this happens whether or not this
      // client has a session cached. Reading first would be enough for the
      // anonymous case (an empty slot sends us into the wait below anyway),
      // but not for adoption: a stale anonymous session from an earlier visit
      // reads back fine, so this client would cache that player, take no wait,
      // and be pointing at the wrong person the moment the adoption lands.
      const scope = this.ctx.config.authScope;
      const settling = signIns.get(scope);
      if (settling) {
        this.ctx.logger.debug('waiting on an identity write in flight for', scope);
        // Its failure belongs to its own caller. All we need is for it to be
        // finished, and then to look at what it left behind.
        await settling.catch(() => {});
      }

      let { data: sess } = await this.ctx.sb.auth.getSession();
      let userId = sess.session?.user?.id;

      if (!userId) {
        // Serialized per auth scope, and NOT by the auth-js lock.
        //
        // auth-js locks on `lock:${storageKey}`, which makes each individual
        // call atomic. This is a check followed by a create — two calls with
        // an await between them — so the lock does not cover it. Two clients
        // sharing a scope, which is the entire point of `authScope`, can both
        // return from getSession() with nothing and both sign in: two
        // anonymous users created, the later response overwriting the shared
        // session, and each client's cached player pointing at a different
        // user than its own later requests authenticate as.
        //
        // Not hypothetical for the case the option was added for. Opening
        // several board clients at once is the natural way to write it.
        // Awaiting a scope-mate's sign-in and then re-reading the session is
        // what makes "share a scope" mean one player rather than usually one.
        // Checked again rather than only above: the read of the slot is an
        // await, so a scope-mate's write can have begun since.
        const inFlight = signIns.get(scope);
        if (inFlight) {
          this.ctx.logger.debug('waiting on a sign-in already in flight for', scope);
          // Its failure belongs to its own caller, not to us. All we need is
          // for it to be finished, and then to look at what it left behind.
          await inFlight.catch(() => {});
          ({ data: sess } = await this.ctx.sb.auth.getSession());
          userId = sess.session?.user?.id;
        }
        if (!userId) {
          this.ctx.logger.debug('no session; signing in anonymously');
          // signInAnonymously requires the project to have
          // `enable_anonymous_sign_ins = true` in its auth config.
          //
          // Registered before the first await, so a scope-mate resuming from
          // its own getSession() cannot slip between the read of the map above
          // and this write. There is no await between them, which is the whole
          // of the guarantee.
          const pending = this.ctx.sb.auth.signInAnonymously();
          // Held the same way an adoption is, and for the same reason: this is
          // a write to the shared slot, and the two kinds of write can race
          // each other as easily as two of a kind. Setting the entry directly
          // here would drop an adoption registered during the getSession()
          // await above, and leave that adoption invisible to whoever came
          // next. holdScope also owns the removal, so there is no `finally`.
          holdScope(scope, pending);
          const { data, error } = await pending;
          if (error || !data.user) {
            throw new VibecadeError(
              VibecadeErrorCode.AuthFailed,
              error?.message ?? 'Could not create anonymous session',
            );
          }
          userId = data.user.id;
        }
      }

      this.authenticated = true;
      // Stamped like every other read. Another context can replace the session
      // while this one is out, and an unstamped write would then put the user
      // init resolved back over the one that is actually signed in - requests
      // authenticated as the newcomer while getPlayer() names the predecessor.
      await this.loadPlayer(userId, ++this.playerLoadGen);
    } catch (e) {
      this.authenticated = false;
      throw wrapError(e, VibecadeErrorCode.AuthFailed, 'SDK initialization failed');
    }
  }

  /**
   * Ask the embedding launcher (if any) to hand over a short-lived
   * access token, so the game iframe and the launcher share an
   * identity. Returns null if not in an iframe, if the launcher never
   * responds, or if the response is malformed.
   *
   * Protocol:
   *   1. SDK posts `{ type: 'vibecade:ready' }` to window.parent.
   *   2. Launcher responds with `{ type: 'vibecade:session',
   *        accessToken }` targeted at the game's origin. Only the
   *        short-lived access token crosses this boundary — never a
   *        refresh token (see IFRAME_PLACEHOLDER_REFRESH_TOKEN). The
   *        launcher later pushes rotated access tokens over the same
   *        message type; see subscribeToParentTokenUpdates().
   *   3. SDK validates shape + parent-source + parent-origin and
   *      resolves the access token plus the validated launcher origin.
   *
   * A 2s timeout keeps the init path snappy for games loaded outside
   * a launcher (direct URL visits, local dev). `targetOrigin: '*'` on
   * the ready post is safe — the announcement contains no secrets; the
   * launcher's response is targeted at the iframe's real origin.
   *
   * Origin validation: `event.source === window.parent` alone is
   * insufficient — a malicious parent can satisfy that check. We
   * additionally compare `event.origin` against the resolved
   * `expectedLauncherOrigin` (see helper below). If we can't determine
   * the parent's origin (no `ancestorOrigins`, no referrer), we fail
   * closed and fall back to anonymous sign-in rather than accept
   * unverified session tokens.
   */
  /**
   * Whether `origin` is a Vibecade launcher, and so may hand this game a
   * session. Built-in origins aside (see isBuiltInLauncherOrigin), that is an
   * https origin whose host is listed in `launchers.hostnames`: the table the
   * launcher itself resolves on every page load, publicly readable, and
   * written only by the platform. Asking it, rather than baking a list in,
   * means a new themed launcher is trusted when it is added there.
   *
   * Not `*.vibecade.ai`: games have been hosted on subdomains of it, and a
   * game is exactly what must not be able to act as a launcher.
   *
   * Fails closed: a lookup that errors or times out is "not trusted", and
   * the game signs in anonymously, as it does when the handshake fails.
   */
  private async isTrustedLauncher(origin: string): Promise<boolean> {
    if (isBuiltInLauncherOrigin(origin)) return true;
    let url: URL;
    try {
      url = new URL(origin);
    } catch {
      return false;
    }
    // A port, a path, or http would each fail to match a listed host as
    // written; checking the shape first also keeps the query to real origins.
    if (url.protocol !== 'https:' || url.origin !== origin) return false;
    try {
      // Through ctx.db like every read: in an iframe with no session its token
      // source answers from the mirror, so this lookup, made mid-handshake,
      // cannot queue a getSession() behind the adoption (see requestToken).
      const { data, error } = await withReadTimeout('launcher origin check', (signal) =>
        this.ctx.db
          .from('launchers')
          .select('id')
          .contains('hostnames', [url.host])
          .abortSignal(signal)
          .limit(1),
      );
      return !error && Array.isArray(data) && data.length > 0;
    } catch {
      return false;
    }
  }

  private tryReceiveParentSession(
    timeoutMs = 2000,
  ): Promise<{ access_token: string; origin: string } | null> {
    if (typeof window === 'undefined') return Promise.resolve(null);
    if (window.parent === window) return Promise.resolve(null);

    const expectedOrigin = expectedLauncherOrigin();
    if (expectedOrigin === null) {
      // Couldn't determine the parent's origin — could be a privacy-
      // hardened browser stripping referrer + missing ancestorOrigins,
      // or an unusual embedding context. Either way we can't validate
      // an incoming session, so don't try; fall through to the
      // anonymous-sign-in path and log so this is debuggable.
      this.ctx.logger.debug(
        'parent-origin unknown; skipping vibecade:session adoption',
      );
      return Promise.resolve(null);
    }

    // Started now, alongside the handshake, so it costs no time when the
    // launcher answers slower than the platform does.
    //
    // The origin checks below establish WHO sent a message; this establishes
    // whether that sender may be believed. Without it the expected origin is
    // whatever page framed the game, so any site could frame a game and hand
    // it a session of the site's choosing: the player then plays, scores and
    // saves into an account the site controls.
    const trusted = this.isTrustedLauncher(expectedOrigin);

    return new Promise((resolve) => {
      let settled = false;
      const finish = (v: { access_token: string; origin: string } | null) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        window.removeEventListener('message', onMessage);
        resolve(v);
      };
      let timer = setTimeout(() => finish(null), timeoutMs);
      let answered = false;
      // An untrusted parent is not waited on: the game falls back at once
      // rather than after the full handshake window.
      void trusted.then((ok) => {
        if (!ok) {
          this.ctx.logger.debug(
            'parent is not a known launcher; not adopting its session',
            expectedOrigin,
          );
          finish(null);
        }
      });

      const ctxLogger = this.ctx.logger;

      function onMessage(ev: MessageEvent): void {
        // Only accept from the embedding window — prevents sibling
        // frames or popups from offering tokens.
        if (ev.source !== window.parent) return;
        // AND from the embedding window's actual origin — `source`
        // alone is satisfiable by any opener that gets a handle to
        // window.parent. Origin is the cryptographic identity of the
        // sender as far as the browser is concerned. Reject silently
        // (don't log per-message — would be noisy if other postMessage
        // chatter shares the page).
        if (ev.origin !== expectedOrigin) return;
        const data = ev.data;
        if (typeof data !== 'object' || data === null) return;
        if ((data as { type?: unknown }).type !== 'vibecade:session') return;

        // ----- Error variant (launcher PR #123 onward) -----
        //
        // The launcher gives up on the session handshake after its own
        // 2-attempt × 5s-timeout chain (cellular stall, Supabase outage,
        // legitimately no-session state). It posts an additive `error`
        // field on the `vibecade:session` message so SDKs can stop
        // waiting and degrade gracefully instead of hanging until our
        // own 2s timeout fires.
        //
        // Why we still finish-with-null rather than throw / surface an
        // error to the caller: the downstream code in `doInit` already
        // handles the null path by falling through to anonymous
        // sign-in, which is the right degraded behavior (game starts
        // in guest mode rather than refusing to run on a flaky
        // network). Surfacing as an explicit error would force every
        // game integrator to handle it, which is more API surface
        // than the benefit warrants right now.
        //
        // Log explicitly though — the difference between "launcher
        // never responded" and "launcher posted an explicit error"
        // matters during incident triage.
        const errorField = (data as { error?: unknown }).error;
        if (typeof errorField === 'string') {
          ctxLogger.debug(
            'received vibecade:session with error field; falling back to local auth',
            errorField,
          );
          finish(null);
          return;
        }

        const accessToken = (data as { accessToken?: unknown }).accessToken;
        if (typeof accessToken !== 'string') return;
        const origin = ev.origin;
        // The launcher has answered, so the handshake window has done its job:
        // it bounds "does anyone answer", not "is the answer trustworthy". Left
        // running, it cut off a lookup slower than 2s and dropped a real
        // launcher's session. The verdict gets its own bound instead, once.
        if (!answered) {
          answered = true;
          clearTimeout(timer);
          timer = setTimeout(() => {
            ctxLogger.debug('launcher answered, but its origin was not confirmed in time', origin);
            finish(null);
          }, LAUNCHER_CHECK_WAIT_MS);
        }
        // Adopted only once the sender is known to be a launcher. finish()
        // ignores a second call, so an untrusted answer that already settled
        // this as null stays null.
        void trusted.then((ok) => {
          if (!ok || settled) return;
          ctxLogger.debug('accepted vibecade:session from', origin);
          // Resolve the access token + the validated launcher origin so
          // the ongoing-rotation listener can pin to the same sender.
          finish({ access_token: accessToken, origin });
        });
      }

      window.addEventListener('message', onMessage);

      try {
        window.parent.postMessage(
          { type: 'vibecade:ready', source: 'vibecade-sdk' },
          '*',
        );
      } catch {
        // Some cross-origin parents refuse postMessage; treat as no-op.
        finish(null);
      }
    });
  }

  /**
   * Install a persistent listener for launcher-pushed access-token
   * rotations while embedded.
   *
   * The iframe holds no refresh token (see
   * IFRAME_PLACEHOLDER_REFRESH_TOKEN) and never auto-refreshes. Instead
   * the launcher — which owns the only real refresh token, in its own
   * origin — posts a fresh `{ type: 'vibecade:session', accessToken }`
   * whenever its Supabase session rotates. We adopt each one via
   * `setSession` (again with the placeholder refresh token) so the
   * in-iframe access token stays valid across long play sessions without
   * ever exposing a renewable credential to game code.
   *
   * `expectedOrigin` is the launcher origin already validated during the
   * initial handshake; every pushed message is re-checked against it AND
   * `window.parent` before adoption, so a co-embedded frame cannot inject
   * a forged token.
   */
  private subscribeToParentTokenUpdates(expectedOrigin: string): void {
    if (typeof window === 'undefined') return;
    // Re-entrant safety: never stack two listeners.
    this.parentTokenUnsub?.();

    const sb = this.ctx.sb;
    const ctxLogger = this.ctx.logger;

    const onMessage = (ev: MessageEvent): void => {
      if (ev.source !== window.parent) return;
      if (ev.origin !== expectedOrigin) return;
      const data = ev.data;
      if (typeof data !== 'object' || data === null) return;
      if ((data as { type?: unknown }).type !== 'vibecade:session') return;
      const accessToken = (data as { accessToken?: unknown }).accessToken;
      if (typeof accessToken !== 'string') return;
      // setSession with an expired token refreshes, with the placeholder, and
      // a refused refresh REMOVES the session: the push meant to keep the
      // player signed in would sign them out. A message delivered late, after
      // the tab slept, is how one arrives expired. Keeping the current session
      // costs nothing; the launcher's next rotation replaces it.
      const expiresAtMs = jwtExpiresAtMs(accessToken);
      if (expiresAtMs !== null && expiresAtMs <= Date.now()) {
        ctxLogger.debug('ignored an expired access token from the launcher');
        return;
      }
      void sb.auth
        .setSession({
          access_token: accessToken,
          refresh_token: IFRAME_PLACEHOLDER_REFRESH_TOKEN,
        })
        .then(({ error }) => {
          if (error) ctxLogger.debug('parent token rotation rejected', error.message);
          else ctxLogger.debug('adopted rotated access token from launcher');
        });
    };

    window.addEventListener('message', onMessage);
    this.parentTokenUnsub = () => window.removeEventListener('message', onMessage);
  }

  private async loadPlayer(userId: string, gen?: number): Promise<Player | null> {
    // Retired BEFORE the outcome is judged, not only before a success is
    // written. A read started for user A that fails while the session has
    // already moved to B is not news about B: it is news about somebody who
    // is no longer signed in. Thrown from here it would reject whoever is
    // awaiting, and for init() that means doInit's catch sets authenticated
    // false and init() rejects, while the B session is live and its own
    // listener may already have loaded B successfully. The caller is told the
    // SDK failed to initialise when it did not.
    //
    // A stale read answers null, exactly as a stale SUCCESS does, so every
    // way of saying "this answer is obsolete" is handled the same way.
    const stale = () => gen !== undefined && gen !== this.playerLoadGen;

    // Wrapped, because a failed read arrives in TWO shapes and only one of
    // them is a value. withReadTimeout's own comment is explicit that an
    // aborted postgrest query usually resolves with `{ error }` but sometimes
    // rejects, and its timeout is a throw either way. A guard that only
    // inspects a resolved value therefore misses the whole rejecting half:
    // the throw happens at this await, before any check can run.
    let data: Record<string, unknown> | null;
    let error: { message?: string } | null;
    try {
      ({ data, error } = await withReadTimeout('player profile read', (signal) =>
        this.ctx.db
          .from('players')
          .select('id, display_name, avatar_url, is_anonymous, created_at')
          .eq('id', userId)
          .abortSignal(signal)
          .maybeSingle(),
      ));
    } catch (e) {
      if (stale()) return null;
      throw e;
    }

    if (error) {
      if (stale()) return null;
      throw new VibecadeError(VibecadeErrorCode.AuthFailed, 'Could not load player profile');
    }
    if (!data) {
      if (stale()) return null;
      // Trigger should have created the row on auth.users insert. If it
      // didn't (misconfigured DB), surface a clear error.
      throw new VibecadeError(
        VibecadeErrorCode.AuthFailed,
        'Player profile not found - platform trigger may be misconfigured',
      );
    }

    const player: Player = {
      id: data.id as string,
      displayName: data.display_name as string,
      avatarUrl: (data.avatar_url as string | null) ?? null,
      isAnonymous: data.is_anonymous as boolean,
      createdAt: data.created_at as string,
    };
    // A reload started by an auth change carries the generation it was
    // started at. If the session has moved again since, this answer is about
    // somebody who is no longer signed in, and writing it would put the WRONG
    // player in the cache - which is the failure the reload exists to fix.
    // Dropping it leaves the cache null, which is honest.
    if (gen !== undefined && gen !== this.playerLoadGen) return null;
    this.ctx.player.current = player;
    this.sessionUserId = userId;
    this.everEstablished = true;
    return player;
  }
}
